import React from 'react';
import { useSearch } from '../../contexts/SearchContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Users, Building2, CreditCard, ArrowRight } from 'lucide-react';
import { StatusBadge } from '../users/StatusBadge';
import { RoleBadge } from '../users/RoleBadge';
import { UserStatus, UserRole } from '../../types/user';

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'user':
      return <Users className='w-4 h-4' />;
    case 'asset':
      return <Building2 className='w-4 h-4' />;
    case 'transaction':
      return <CreditCard className='w-4 h-4' />;
    default:
      return <Building2 className='w-4 h-4' />;
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'user':
      return 'bg-blue-500/10 text-blue-600 border-blue-200';
    case 'asset':
      return 'bg-green-500/10 text-green-600 border-green-200';
    case 'transaction':
      return 'bg-purple-500/10 text-purple-600 border-purple-200';
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-200';
  }
};

export const SearchResults: React.FC = () => {
  const { searchQuery, searchResults, isSearching, clearSearch } = useSearch();
  const navigate = useNavigate();

  const handleResultClick = (url: string) => {
    navigate(url);
  };

  const handleClearSearch = () => {
    clearSearch();
    navigate('/dashboard');
  };

  if (isSearching) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <Loader2 className='w-8 h-8 animate-spin mx-auto mb-4 text-primary' />
          <p className='text-muted-foreground'>Searching...</p>
        </div>
      </div>
    );
  }

  if (!searchQuery) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <div className='w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4'>
            <Building2 className='w-8 h-8 text-muted-foreground' />
          </div>
          <h3 className='text-lg font-semibold mb-2'>Start Searching</h3>
          <p className='text-muted-foreground'>
            Enter a search term to find users, assets, or transactions
          </p>
        </div>
      </div>
    );
  }

  if (searchResults.length === 0) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <div className='w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4'>
            <Building2 className='w-8 h-8 text-muted-foreground' />
          </div>
          <h3 className='text-lg font-semibold mb-2'>No Results Found</h3>
          <p className='text-muted-foreground mb-4'>
            No results found for &ldquo;<span className='font-medium'>{searchQuery}</span>&rdquo;
          </p>
          <Button onClick={handleClearSearch} variant='outline'>
            Clear Search
          </Button>
        </div>
      </div>
    );
  }

  const userResults = searchResults.filter((result) => result.type === 'user');
  const assetResults = searchResults.filter((result) => result.type === 'asset');
  const transactionResults = searchResults.filter((result) => result.type === 'transaction');

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold'>Search Results</h1>
          <p className='text-muted-foreground'>
            Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;
            <span className='font-medium'>{searchQuery}</span>&rdquo;
          </p>
        </div>
        <Button onClick={handleClearSearch} variant='outline'>
          Clear Search
        </Button>
      </div>

      {/* User Results */}
      {userResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Users className='w-5 h-5' />
              Users ({userResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            {userResults.map((result) => (
              <div
                key={result.id}
                className='flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors'
                onClick={() => handleResultClick(result.url)}
              >
                <div className='flex items-center gap-3'>
                  <div className={`p-2 rounded-lg border ${getTypeColor(result.type)}`}>
                    {getTypeIcon(result.type)}
                  </div>
                  <div>
                    <h4 className='font-medium'>{result.title}</h4>
                    <p className='text-sm text-muted-foreground'>{result.subtitle}</p>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  {result.metadata?.status && (
                    <StatusBadge status={result.metadata.status as UserStatus} />
                  )}
                  {result.metadata?.role && <RoleBadge role={result.metadata.role as UserRole} />}
                  <ArrowRight className='w-4 h-4 text-muted-foreground' />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Asset Results */}
      {assetResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Building2 className='w-5 h-5' />
              Assets ({assetResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            {assetResults.map((result) => (
              <div
                key={result.id}
                className='flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors'
                onClick={() => handleResultClick(result.url)}
              >
                <div className='flex items-center gap-3'>
                  <div className={`p-2 rounded-lg border ${getTypeColor(result.type)}`}>
                    {getTypeIcon(result.type)}
                  </div>
                  <div>
                    <h4 className='font-medium'>{result.title}</h4>
                    <p className='text-sm text-muted-foreground'>{result.subtitle}</p>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  {result.metadata?.type && (
                    <Badge variant='secondary'>{result.metadata.type}</Badge>
                  )}
                  {result.metadata?.status && (
                    <StatusBadge status={result.metadata.status as UserStatus} />
                  )}
                  <ArrowRight className='w-4 h-4 text-muted-foreground' />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Transaction Results */}
      {transactionResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <CreditCard className='w-5 h-5' />
              Transactions ({transactionResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            {transactionResults.map((result) => (
              <div
                key={result.id}
                className='flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors'
                onClick={() => handleResultClick(result.url)}
              >
                <div className='flex items-center gap-3'>
                  <div className={`p-2 rounded-lg border ${getTypeColor(result.type)}`}>
                    {getTypeIcon(result.type)}
                  </div>
                  <div>
                    <h4 className='font-medium'>{result.title}</h4>
                    <p className='text-sm text-muted-foreground'>{result.subtitle}</p>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  {result.metadata?.amount && (
                    <Badge variant='outline'>{result.metadata.amount}</Badge>
                  )}
                  <ArrowRight className='w-4 h-4 text-muted-foreground' />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
